"use client";

import { c as _c } from "react/compiler-runtime";
import { Slot } from "@radix-ui/react-slot";
import * as React from "react";
import { Controller, FormProvider, useFormContext } from "react-hook-form";
import { Label } from "@/lib/components/label";
import { cn } from "@/lib/utils";
import type * as LabelPrimitive from "@radix-ui/react-label";
import type { ControllerProps, FieldPath, FieldValues } from "react-hook-form";
const Form = FormProvider;
interface FormFieldContextValue<TFieldValues extends FieldValues = FieldValues, TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>> {
  name: TName;
}
const FormFieldContext = React.createContext<FormFieldContextValue>({} as FormFieldContextValue);
const FormField = t0 => {
  const $ = _c(9);
  let props;
  if ($[0] !== t0) {
    ({
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
  } else {
    props = $[1];
  }
  let t1;
  if ($[2] !== props.name) {
    t1 = {
      name: props.name
    };
    $[2] = props.name;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== props) {
    t2 = <Controller {...props} />;
    $[4] = props;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t1 || $[7] !== t2) {
    t3 = <FormFieldContext value={t1}>{t2}</FormFieldContext>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
};
const useFormField = () => {
  const $ = _c(11);
  const fieldContext = React.use(FormFieldContext);
  const itemContext = React.use(FormItemContext);
  const {
    getFieldState,
    formState
  } = useFormContext();
  let t0;
  if ($[0] !== fieldContext.name || $[1] !== formState || $[2] !== getFieldState) {
    t0 = getFieldState(fieldContext.name, formState);
    $[0] = fieldContext.name;
    $[1] = formState;
    $[2] = getFieldState;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const fieldState = t0;
  if (!fieldContext) {
    throw new Error("useFormField should be used within <FormField>");
  }
  const {
    id
  } = itemContext;
  const t1 = `${id}-form-item`;
  const t2 = `${id}-form-item-description`;
  const t3 = `${id}-form-item-message`;
  let t4;
  if ($[4] !== fieldContext.name || $[5] !== fieldState || $[6] !== id || $[7] !== t1 || $[8] !== t2 || $[9] !== t3) {
    t4 = {
      id,
      name: fieldContext.name,
      formItemId: t1,
      formDescriptionId: t2,
      formMessageId: t3,
      ...fieldState
    };
    $[4] = fieldContext.name;
    $[5] = fieldState;
    $[6] = id;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
};
interface FormItemContextValue {
  id: string;
}
const FormItemContext = React.createContext<FormItemContextValue>({} as FormItemContextValue);
const FormItem = t0 => {
  const $ = _c(15);
  let className;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      ref,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = ref;
  } else {
    className = $[1];
    props = $[2];
    ref = $[3];
  }
  const id = React.useId();
  let t1;
  if ($[4] !== id) {
    t1 = {
      id
    };
    $[4] = id;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== className) {
    t2 = cn("space-y-2", className);
    $[6] = className;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== props || $[9] !== ref || $[10] !== t2) {
    t3 = <div ref={ref} className={t2} {...props} />;
    $[8] = props;
    $[9] = ref;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== t1 || $[13] !== t3) {
    t4 = <FormItemContext value={t1}>{t3}</FormItemContext>;
    $[12] = t1;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  return t4;
};
FormItem.displayName = "FormItem";
const FormLabel = t0 => {
  const $ = _c(12);
  let className;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      ref,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = ref;
  } else {
    className = $[1];
    props = $[2];
    ref = $[3];
  }
  const {
    error,
    formItemId
  } = useFormField();
  const t1 = error && "text-destructive";
  let t2;
  if ($[4] !== className || $[5] !== t1) {
    t2 = cn(t1, className);
    $[4] = className;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== formItemId || $[8] !== props || $[9] !== ref || $[10] !== t2) {
    t3 = <Label ref={ref} className={t2} htmlFor={formItemId} {...props} />;
    $[7] = formItemId;
    $[8] = props;
    $[9] = ref;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
};
FormLabel.displayName = "FormLabel";
const FormControl = t0 => {
  const $ = _c(9);
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
    $[2] = ref;
  } else {
    props = $[1];
    ref = $[2];
  }
  const {
    error,
    formItemId,
    formDescriptionId,
    formMessageId
  } = useFormField();
  const t1 = !error ? `${formDescriptionId}` : `${formDescriptionId} ${formMessageId}`;
  const t2 = !!error;
  let t3;
  if ($[3] !== formItemId || $[4] !== props || $[5] !== ref || $[6] !== t1 || $[7] !== t2) {
    t3 = <Slot ref={ref} id={formItemId} aria-describedby={t1} aria-invalid={t2} {...props} />;
    $[3] = formItemId;
    $[4] = props;
    $[5] = ref;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
};
FormControl.displayName = "FormControl";
const FormDescription = t0 => {
  const $ = _c(11);
  let className;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      ref,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = ref;
  } else {
    className = $[1];
    props = $[2];
    ref = $[3];
  }
  const {
    formDescriptionId
  } = useFormField();
  let t1;
  if ($[4] !== className) {
    t1 = cn("text-muted-foreground text-[0.8rem]", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== formDescriptionId || $[7] !== props || $[8] !== ref || $[9] !== t1) {
    t2 = <p ref={ref} id={formDescriptionId} className={t1} {...props} />;
    $[6] = formDescriptionId;
    $[7] = props;
    $[8] = ref;
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
};
FormDescription.displayName = "FormDescription";
const FormMessage = t0 => {
  const $ = _c(13);
  let children;
  let className;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      ref,
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = ref;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    ref = $[4];
  }
  const {
    error,
    formMessageId
  } = useFormField();
  const body = error ? String(error?.message) : children;
  if (!body) {
    return null;
  }
  let t1;
  if ($[5] !== className) {
    t1 = cn("text-destructive text-[0.8rem] font-medium", className);
    $[5] = className;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== body || $[8] !== formMessageId || $[9] !== props || $[10] !== ref || $[11] !== t1) {
    t2 = <p ref={ref} id={formMessageId} className={t1} {...props}>{body}</p>;
    $[7] = body;
    $[8] = formMessageId;
    $[9] = props;
    $[10] = ref;
    $[11] = t1;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  return t2;
};
FormMessage.displayName = "FormMessage";
export { useFormField, Form, FormItem, FormLabel, FormControl, FormDescription, FormMessage, FormField };